import { useEffect, useRef, useState } from 'react';
import styles from './LoginPage.module.css'
import ReCAPTCHA from 'react-google-recaptcha';
import { useReferenceDataContext } from '../context/ReferenceDataContext';
import authService from '../services/authService';
import { Link, useNavigate } from 'react-router-dom';
import { findClosestTimezone } from '../utils/timezoneUtils';
import { useAppContext } from '../context/AppContext';
import { SellerModel } from '../models/SellerModel';
import { SellerAccountModel } from '../models/SellerAccountModel';
import TermsAndConditionCheckboxComp from '../components/TermsAndConditionCheckboxComp';

interface RegistrationErrors {
    username?: string;
    password?: string;
    confirmPassword?: string;
    firstname?: string;
    lastname?: string;
    storeName?: string;
    termsAndConditions?: string;
}

const SignUpPage: React.FC = () => {
    const { timezones } = useReferenceDataContext();
    const { setSeller, setSellerAccount } = useAppContext();
    const navigate = useNavigate();

    const [captchaValue, setCaptchaValue] = useState<string | null>(null);
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstname, setFirstname] = useState('');
    const [lastname, setLastname] = useState('');
    const [storeName, setStoreName] = useState('');
    const [timezone, setTimezone] = useState('');
    const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] = useState(false);
    const [registrationError, setRegistrationError] = useState<RegistrationErrors>({});

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {

        if (timezones && timezones.length > 0) {
            const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const match = findClosestTimezone(timezones, browserTz);
            setTimezone(match?.timezoneId || '');
        }
    }, [timezones]);

    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                navigate('/'); // Redirect to home page
            }, 5000); // 5000ms = 5 seconds

            return () => clearTimeout(timer); // Clean up if component unmounts
        }
    }, [success, navigate]);

    const handleCaptchaChange = (value: string | null): void => {
        setCaptchaValue(value);
        console.log('reCaptcha token:', value);
    };

    const handleCaptchaExpired = (): void => {
        setCaptchaValue(null);
        setError('reCAPTCHA expired. Please verify again.');
    };

    const handleCaptchaError = (): void => {
        setCaptchaValue(null);
        setError('reCAPTCHA error occurred. Please try again.');
    };

    const validateRegistration = () => {
        let errors: RegistrationErrors = {};
        if (!username) {
            errors.username = "Please enter a username";
        }
        // Simple email format check
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (username && !emailRegex.test(username)) {
            errors.username = "Please enter a valid email address";
        }

        if (!password) {
            errors.password = "Please enter a password";
        }

        if (password && password.length < 6) {
            errors.password = "Password must be at least 6 characters long";
        }
        if (!confirmPassword) {
            errors.confirmPassword = "Please confirm your password";
        }

        if (confirmPassword.length < 6) {
            errors.confirmPassword = "Confirm Password must be at least 6 characters long";
        }
        if (password !== confirmPassword) {
            errors.confirmPassword = "Passwords do not match";
        }
        if (!firstname) {
            errors.firstname = "Please enter your first name";
        }
        if (!lastname) {
            errors.lastname = "Please enter your last name";
        }
        if (!storeName) {
            errors.storeName = "Please enter your store name";
        }
        if (!acceptedTermsAndConditions) {
            errors.termsAndConditions = "Please review and accept the terms and conditions.";
        }


        setRegistrationError(errors);
        return Object.keys(errors).length === 0;
    }


    const handleRegister = () => {
        const registerSellerAccount = async () => {
            try {
                const result = await authService.register(
                    username, password, firstname, lastname, storeName, timezone, captchaValue!
                );

                if (result.registerState === "Success" && result.seller && result.sellerAccount) {
                    setSeller(new SellerModel(result.seller));
                    setSellerAccount(new SellerAccountModel(result.sellerAccount));
                    setSuccess('Your account has been created successfully.');
                } else {
                    // Reset reCAPTCHA on failure
                    recaptchaRef.current?.reset();
                    setCaptchaValue(null);

                    if (result.registerState === "UsernameExist") {
                        setError('The email you entered already exists, please choose a different email.');
                    } else if (result.registerState === "IncompleteFields") {
                        setError('Please fill in all required fields.');
                    } else if (result.registerState === "InvalidReCaptchaToken") {
                        setError('Invalid reCaptcha token.');
                    } else if (result.registerState === "InvalidUsernameFormat") {
                        setError('Invalid username format. Please try again later.');
                    } else if (result.registerState === "PasswordTooShort") {
                        setError('Password must be at least 6 characters long.');
                    } else {
                        setError('An unknown error occurred while creating your account. Please try again later.');
                    }
                }
            } catch (error) {
                recaptchaRef.current?.reset();
                setCaptchaValue(null);
                setError('An error occurred while creating your account. Please try again later.');
            } finally {
                setIsSubmitting(false);
            }
        }

        if (validateRegistration()) {
            if (!captchaValue) {
                setError('Please complete the reCAPTCHA verification.');
                return;
            }
            setIsSubmitting(true);
            registerSellerAccount();
        }
    };



    return (
        <>
            <div className='w-100 m-auto' style={{ maxWidth: '800px', padding: "1rem" }}>
                <div className='d-flex justify-content-lg-center'>
                    <Link to="/"> <img className={styles.loginLogo} style={{ marginTop: '40px', marginBottom: '40px' }} src="/images/tahpi-plus.png" alt=""></img></Link>
                </div>
                <h3 className={`mb-4 text-center ${styles.title}`}>Create New Account</h3>
                {!success && <>
                    <div className='row g-3'>
                        <div className='col-md-12'>
                            <div className={`${styles.formFloatingZindex} form-floating mb-2`}>
                                <input type="text" className={`form-control ${registrationError.username ? ` is-invalid` : ``}`} placeholder="Username" value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                ></input>
                                <label>Email</label>
                                {registrationError.username && <div className="invalid-feedback">{registrationError.username}</div>}
                            </div>
                        </div>
                        <div className='col-md-6'>
                            <div className={`${styles.formFloatingZindex} form-floating mb-2`}>
                                <input type="text" className={`form-control ${registrationError.firstname ? ` is-invalid` : ``}`} placeholder="First Name" value={firstname}
                                    onChange={(e) => setFirstname(e.target.value)}
                                ></input>
                                <label>First Name</label>
                                {registrationError.firstname && <div className="invalid-feedback">{registrationError.firstname}</div>}
                            </div>
                        </div>
                        <div className='col-md-6'>
                            <div className={`${styles.formFloatingZindex} form-floating mb-2`}>
                                <input type="text" className={`form-control ${registrationError.lastname ? ` is-invalid` : ``}`} placeholder="Last Name" value={lastname}
                                    onChange={(e) => setLastname(e.target.value)}
                                ></input>
                                <label>Last Name</label>
                                {registrationError.lastname && <div className="invalid-feedback">{registrationError.lastname}</div>}
                            </div>
                        </div>
                        <div className='col-md-12'>
                            <div className={`${styles.formFloatingZindex} form-floating mb-2`}>
                                <input type="text" className={`form-control ${registrationError.storeName ? ` is-invalid` : ``}`} placeholder="Store Name" value={storeName}
                                    onChange={(e) => setStoreName(e.target.value)}
                                ></input>
                                <label>Store Name</label>
                                {registrationError.storeName && <div className="invalid-feedback">{registrationError.storeName}</div>}
                            </div>
                        </div>
                        <div className='col-md-6'>
                            <div className={`${styles.formFloatingZindex} form-floating mb-2`}>
                                <input type="password" className={`form-control ${registrationError.password ? ` is-invalid` : ``}`} placeholder="Password" value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                ></input>
                                <label>Password</label>
                                {registrationError.password && <div className="invalid-feedback">{registrationError.password}</div>}
                            </div>
                        </div>
                        <div className='col-md-6'>
                            <div className={`${styles.formFloatingZindex} form-floating mb-2`}>
                                <input type="password" className={`form-control ${registrationError.confirmPassword ? ` is-invalid` : ``}`} placeholder="Confirm Password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}></input>
                                <label>Confirm Password</label>
                                {registrationError.confirmPassword && <div className="invalid-feedback">{registrationError.confirmPassword}</div>}
                            </div>
                        </div>

                    </div>
                    <div className="mt-3">
                        <TermsAndConditionCheckboxComp
                            checked={acceptedTermsAndConditions}
                            onChange={(val) => {
                                setAcceptedTermsAndConditions(val);
                               
                                if (val) {
                                    setRegistrationError((prev) => ({ ...prev, termsAndConditions: undefined }));
                                }
                            }}
                            
                        />
                        {registrationError.termsAndConditions && (
                            <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>
                                {registrationError.termsAndConditions}
                            </div>
                        )}
                    </div>
                    <div className='mt-3'>
                        <ReCAPTCHA ref={recaptchaRef} sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""} onChange={handleCaptchaChange} onExpired={handleCaptchaExpired} onError={handleCaptchaError} />
                    </div>
                    {error && <div className="alert alert-danger mt-2" role="alert">{error}</div>}
                    <button className="btn btn-lg btn-dark w-100 py-2 my-3 rounded-0" onClick={handleRegister} disabled={isSubmitting}>
                        {isSubmitting ? (<><span className={`spinner-border ${styles.formSigninSpinner}`} role="status" aria-hidden="true"></span> Creating your account ...</>) : ("Create Account")}
                    </button>
                </>
                }
                {success && (
                    <div className="row justify-content-center">
                        <div className="col-md-10">
                            <div className="alert alert-success text-center" role="alert">
                                {success}
                                <br />
                                <small>You will be redirected to the home page in 3 seconds...</small>
                            </div>
                        </div>
                    </div>
                )}





            </div>



        </>
    );
}
export default SignUpPage;